import {
    Directive,
    ElementRef,
    Renderer2,
    AfterViewInit,
    Input,
} from '@angular/core';

@Directive({
    selector: '[resizableColumn]'
})
export class ResizableColumnDirective implements AfterViewInit {

    @Input() tableId: string = "default-table"; // used for localStorage
    @Input() colKey: string = "";              // unique column name
    @Input() minWidth: number = 80;            // minimum width

    private startX!: number;
    private startWidth!: number;
    private resizer!: HTMLElement;

    constructor(
        private el: ElementRef,
        private renderer: Renderer2
    ) { }

    ngAfterViewInit() {
        const th = this.el.nativeElement;
        th.style.position = "relative";

        /** Restore saved column width */
        this.loadSavedWidth(th);

        /** Create the resizer element */
        this.resizer = this.renderer.createElement("div");
        this.renderer.addClass(this.resizer, "col-resizer");
        this.renderer.appendChild(th, this.resizer);

        /** Mouse events */
        this.resizer.addEventListener("mousedown", (event: MouseEvent) =>
            this.startResize(event, th)
        );

        /** Double-click auto-fit */
        this.resizer.addEventListener("dblclick", () => this.autoFit(th));
    }

    /** Start resizing */
    startResize(event: MouseEvent, th: HTMLElement) {
        event.preventDefault();

        this.startX = event.pageX;
        this.startWidth = th.offsetWidth;

        const mouseMoveHandler = (e: MouseEvent) => this.resize(e, th);
        const mouseUpHandler = () => {
            document.removeEventListener("mousemove", mouseMoveHandler);
            document.removeEventListener("mouseup", mouseUpHandler);

            /** Save final width */
            this.saveWidth(th);
        };

        document.addEventListener("mousemove", mouseMoveHandler);
        document.addEventListener("mouseup", mouseUpHandler);
    }

    /** Perform resize */
    resize(event: MouseEvent, th: HTMLElement) {
        const newWidth = Math.max(this.minWidth, this.startWidth + (event.pageX - this.startX));
        th.style.width = `${newWidth}px`;
    }

    /** Auto-fit using double-click */
    autoFit(th: HTMLElement) {
        const table = th.closest("table");
        const columnIndex = Array.from(th.parentElement!.children).indexOf(th);

        let maxWidth = this.minWidth;

        // Check the header text width
        maxWidth = Math.max(maxWidth, th.scrollWidth + 20);

        // Check all rows
        table!.querySelectorAll("tbody tr").forEach((row: any) => {
            const cell = row.children[columnIndex];
            if (cell) {
                maxWidth = Math.max(maxWidth, cell.scrollWidth + 20);
            }
        });

        th.style.width = `${maxWidth}px`;

        this.saveWidth(th);
    }

    /** Save width in LocalStorage */
    saveWidth(th: HTMLElement) {
        if (!this.colKey) return;
        const storageKey = `${this.tableId}_${this.colKey}`;
        localStorage.setItem(storageKey, th.style.width);
    }

    /** Load saved width */
    loadSavedWidth(th: HTMLElement) {
        if (!this.colKey) return;
        const storageKey = `${this.tableId}_${this.colKey}`;
        const savedWidth = localStorage.getItem(storageKey);

        if (savedWidth) th.style.width = savedWidth;
    }
}
